/**
 * @module AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  HostBinding,
  Output,
  EventEmitter,
} from '@angular/core';
// import { HttpResponse } from '@angular/common/http';
import { AuthorableComponent } from '@core/aem/models/authorable-component.model';
import { ContainerHolderData } from '@core/aem/components/container-holder/models/container-holder-data.interface';
import { WorkerService } from '@core/worker/services/worker.service';
import { Content } from '@core/aem/models/content.interface';
import { ContentItem } from '@core/aem/models/content-item.interface';
import { ContainerData } from '@core/aem/components/container/models/container-data.interface';
import { ContentResolver } from '@core/aem/models/content-resolver';
import { ContentPathsService } from '@core/aem/services/content-paths.service';
import { AppConfig } from '@root/app.config';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { CONTAINER_CONSTANTS } from '@core/aem/components/container-holder/container-holder.constant';
import { MarketingService } from '@root/shared/services/marketing.service';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { CHANNELS, SETTINGS } from '@environment/settings';
import { SeoService } from '@core/services/seo.service';
import { HttpResponse } from '@angular/common/http';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
@Component({
  selector: 'tmo-container-holder',
  templateUrl: './container-holder.component.html',
})
export class ContainerHolderComponent
  extends AuthorableComponent<ContainerHolderData>
  implements OnInit, OnDestroy, OnChanges
{
  /**
   * String Value to store id
   */
  @Input() public id: string;
  /**
   * Varibale to store container Data
   */
  @Input() public items: ContentItem<ContainerData>[];
  /**
   * Varibale to store DynamicContent data
   */
  @Input() public isDynamicContent: ContentItem<ContainerData>[];
  /**
   * Varibale for emit  pageloaded event
   */
  @Output() public pageLoadedEvent: EventEmitter<boolean>;
  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  @HostBinding('class.row') private row: boolean;
  constructor(
    private workerService: WorkerService,
    private contentPath: ContentPathsService,
    private appConfig: AppConfig,
    private seoService: SeoService,
    private marketingService: MarketingService,
    private pdlWrapperService: PdlWrapperService,
    private pdpDataHandlerService: PdpDataHandlerService
  ) {
    super();
    this.items = [];
    this.pageLoadedEvent = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }

  public ngOnInit(): void {
    this.row = true;
    // this.test = true;
    // Parent container holders have ID's inserted by the UI
    // Child containers are based from AEM metadata -
    // the setData method is called from our AEM factory so we don't need to worry about populating child containers
    if (this.id || this.isDynamicContent) {
      this.load();
      this.row = false;
    }
  }

  /**
   *  Method  for DynamicContent changes.
   */
  public ngOnChanges(): void {
    if (this.isDynamicContent) {
      this.load();
      this.row = false;
    }
  }

  /**
   * Sets the data for the specified component. This is required as the AEM module will use this method to populate
   * component data
   * @param data Input data for AEM component
   */
  public setData(data: ContainerHolderData): void {
    if (this.validate(data)) {
      this.items = this.generateComponents(data.items);
    }
  }

  /**
   * Check if the data is valid, should throw an error if the input doesn't match designated fields
   * @param data
   */
  public validate(data: ContainerHolderData): boolean {
    if (this.isDynamicContent) {
      return true;
    } else {
      if (!data.items) {
        throw new Error(CONTAINER_CONSTANTS.INVAILD_CONTAINER_INPUT);
      }
      const itemLength = data.items.length;
      for (let i: number = 0; i < itemLength; i++) {
        if (!data.items[i].uiComponent || !data.items[i].data) {
          throw new Error(CONTAINER_CONSTANTS.INVALID_EXPECTED_CONTAINER_INPUT);
        }
      }
      return true;
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribe.unsubscribe();
  }

  /**
   * Loads the container holder and dynamically populates metadata
   * This is how our dynamic components are inserted
   */
  private load(): void {
    // Fetch data based on ID
    // @TODO remove mock request
    const domain = this.appConfig.getConfig('aemEndPoint');
    const aemVersion = this.appConfig.getConfig('aemContentVersion')
      ? this.appConfig.getConfig('aemContentVersion')
      : '1.0';
    if (!this.isDynamicContent) {
      this.contentPath.getPath(this.id).then(
        (path: string) => {
          const aemPath: string = domain.concat(path);
          const workerArg: WorkerRequestArgs = {
            headers: null,
            preferCache: true,
            cacheResponse: true,
            cacheKey: path.concat(aemVersion),
          };
          return this.workerService
            .get(aemPath, workerArg)
            .pipe(
              map((res: HttpResponse<Object>) => {
                return res.body;
              }),
              takeUntil(this.ngUnsubscribe.asObservable())
            )
            .subscribe((res: Content) => {
              if (
                this.id === CONTENT_ENDPOINT.HOME ||
                this.id === CONTENT_ENDPOINT.COVERAGE_MAP ||
                this.id === CONTENT_ENDPOINT.WEB_HOME
              ) {
                this.pdlWrapperService.trackData(this.id, 'PAGE_LOAD', res);
                if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB) {
                  const userData = this.pdpDataHandlerService.setUserData();
                  this.pdlWrapperService.trackData('Product-Detail', 'product', { user: userData });
                }
              }
              const items: ContentItem<ContainerData>[] =
                res.model['model-data']['module-enhancements'];
              this.loadItems(items, res);
              this.marketingService.send(res);
              if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB && res && res.metadata) {
                this.seoService.setSeoMetadata(res);
              }
            });
        },
        (err: string) => {
          throw new Error(CONTAINER_CONSTANTS.FAILED_CONTENT);
        }
      );
    } else {
      this.loadItems(this.isDynamicContent);
    }
  }

  /**
   * Load Dynamic Components
   */
  private generateComponents(items: ContentItem<ContainerData>[]): ContentItem<ContainerData>[] {
    return ContentResolver.generateComponents(items);
  }

  /**
   * load items coming from AEM
   * @param items item array from which components will be loaded
   * @param res response from AEM
   * @return void
   */
  private loadItems(items: ContentItem<ContainerData>[], res?: Content): void {
    if (items.length > 0) {
      if (items[0].uiComponent) {
        this.setData({ items: items });
      } else {
        throw new Error(CONTAINER_CONSTANTS.INVALID_AEM_CONTRACT);
      }
    } else {
      throw new Error(CONTAINER_CONSTANTS.INVALID_AEM_CONTRACT);
    }
    this.pageLoadedEvent.emit(true);
  }
}
